package com.appcreator.compose

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Modifier
import coil3.ImageLoader
import coil3.PlatformContext
import coil3.compose.LocalPlatformContext
import coil3.compose.setSingletonImageLoaderFactory
import coil3.disk.DiskCache
import coil3.memory.MemoryCache
import coil3.request.crossfade
import coil3.svg.SvgDecoder
import com.appcreator.blueprint.Blueprint
import com.appcreator.blueprint.Destination
import com.appcreator.blueprint.core.EnvStore
import com.appcreator.blueprint.core.ReadonlyEnvStore
import com.appcreator.compose.components.InputEnvStore
import com.appcreator.compose.components.basic.VideoCastBottomBar
import com.appcreator.compose.di.Container
import com.appcreator.compose.di.Setup
import com.appcreator.compose.di.performer
import com.appcreator.compose.di.platformGifFactory
import com.appcreator.compose.envfunctions.rememberEnvFunctions
import com.appcreator.compose.interfaces.PushDataType
import com.appcreator.compose.interfaces.PushHandler
import com.appcreator.compose.theme.Theme
import kotlinx.coroutines.launch
import okio.Path
import org.jetbrains.compose.resources.FontResource

data class BlueprintConfig(
    val iosAppStoreAppId: String = ""
)

fun interface BlueprintProvider {
    fun provideBlueprint(): State<Blueprint?>
}

@Composable
fun BlueprintComposable(
    drawableResource: (String) -> String?,
    fontResources: Map<String, FontResource>,
    provider: BlueprintProvider = Setup.blueprintProvider,
    rootScreen: (Blueprint) -> Destination = { Destination(it.rootDestination().screen, emptyMap()) },
    config: BlueprintConfig = BlueprintConfig()
) {

    val platformContext = LocalPlatformContext.current
    setSingletonImageLoaderFactory {
        ImageLoader.Builder(it)
            // Add a marker value so we know this was created by the default singleton image loader.
            .components {
                add(platformGifFactory())
                add(SvgDecoder.Factory())
                Container.fetchers.forEach { fetcher ->
                    add(fetcher)
                }
//                add(UrlKeyer())
            }
            .memoryCache {
                MemoryCache.Builder()
                    .maxSizePercent(platformContext, 0.25)
                    .build()
            }
            .diskCache {
                imageCacheDirectory(platformContext)?.let { directory ->
                    DiskCache.Builder()
                        .directory(directory)
                        .maxSizePercent(0.02)
                        .build()
                }
            }

            .crossfade(true)
            .build()
    }

    val envStore = LocalEnvStore.current
    val nullableBlueprint by remember { provider.provideBlueprint() }
    val permissions by getPermissions()
    nullableBlueprint?.let { blueprint ->
        val globalDefaults = remember {
            (blueprint.globalData
                ?.filter { envStore.env[it.key] == null }
                ?.associate { data ->
                    data.key to data.value(blueprint.defaultEnvironment)
                } ?: emptyMap()
            ) + Container.globalKeys
        }

        permissions?.let {
            val envFunctions = rememberEnvFunctions(config)
            val globalState = remember {
                InputEnvStore(
                    initial = globalDefaults + it,
                    key = null,
                    persistent = false,
                    parent = envStore,
                    defaultLocale = blueprint.defaultLocale,
                )
            }

            val finalEnvStore = ReadonlyEnvStore.create(
                parent = globalState,
                env = envStore.env + envFunctions,
            )

            GlobalActions(blueprint, finalEnvStore)

            blueprint.Theme {
                CompositionLocalProvider(
                    LocalBlueprint provides blueprint,
                    LocalTheme provides blueprint.theme,
                    LocalInputEnvStore provides globalState,
                    LocalEnvStore provides finalEnvStore,
                    LocalResources provides drawableResource,
                    LocalFontResources provides fontResources
                ) {
                    SnackbarAlertContainer(
                        content = {
                            InAppNotificationWrapper {
                                ScreenComposable(rootScreen(blueprint))
                            }
                        },
                        footer = {
                            VideoCastBottomBar(Modifier, it)
                        }
                    )
                }
            }
        }
    }
}

@Composable
private fun GlobalActions(blueprint: Blueprint, envStore: EnvStore) {
    val scope = rememberCoroutineScope()
    val deferred = blueprint.globalActions?.newPushToken?.let { Container.performer(it) }?.deferred()
    DisposableEffect(Unit) {
        val pushHandler = object : PushHandler {
            override suspend fun onNewToken(data: PushDataType) {
                scope.launch { deferred?.perform(envStore) }
            }
        }
        Container.pushHandler.addChild(pushHandler)
        onDispose {
            Container.pushHandler.removeChild(pushHandler)
        }
    }
}

@Composable
expect fun getPermissions(): State<Map<String, String>?>

@Composable
expect fun InAppNotificationWrapper(content: @Composable () -> Unit)

expect fun imageCacheDirectory(context: PlatformContext): Path?